import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessibilityControls } from './components/AccessibilityControls';
import {
  A11Y_STORAGE_KEY,
  getInitialA11yScheme,
  getInitialHoverZoomOthers,
  syncHoverZoomOthersToDom,
} from './accessibility/a11yPreferences';
import { localizationService } from './services/localizationService';
import { useManagedLanguages } from './hooks/useManagedLanguages';
import { languageManifest as baseLanguageManifest } from './locales';
import { en as enMaster } from './locales/en';
import { ACCREDITATION_WIZARD_EN } from './locales/accreditationWizardEn';
import { getTranslatingIndicator } from './locales/translatingIndicator';
import {
  outcomeToRegisterLevel,
  scorePath1,
  scorePath2,
} from './utils/accreditationWizardLogic';
import './app.css';
import './accreditation-wizard.css';

const PATH1_KEYS = ['q1a', 'q1b', 'q1c'];
const PATH2_KEYS = ['q2a', 'q2b', 'q2c'];

function ChoiceGroup({ choices, value, onSelect, choicePrefix }) {
  return (
    <div className="accreditation-wizard-choices" role="radiogroup">
      {choices.map((text, idx) => {
        const num = idx + 1;
        const selected = value === num;
        return (
          <button
            key={num}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`accreditation-wizard-choice${selected ? ' accreditation-wizard-choice--selected' : ''}`}
            onClick={() => onSelect(num)}
          >
            <span className="accreditation-wizard-choice__num">
              {choicePrefix} {num}
            </span>
            <span className="accreditation-wizard-choice__text">{text}</span>
          </button>
        );
      })}
    </div>
  );
}

function WizardStep({
  id,
  stepTitle,
  question,
  summary,
  status,
  isExpanded,
  onToggle,
  children,
}) {
  const canToggle = status === 'done';
  return (
    <section
      className={`accreditation-wizard-step accreditation-wizard-step--${status}`}
      aria-expanded={isExpanded}
    >
      <button
        type="button"
        className="accreditation-wizard-step__head"
        onClick={() => canToggle && onToggle(id)}
        disabled={!canToggle && status !== 'active'}
        aria-controls={`wizard-panel-${id}`}
        id={`wizard-trigger-${id}`}
      >
        <div>
          <p className="accreditation-wizard-step__title">{stepTitle}</p>
          <p className="accreditation-wizard-step__question">{question}</p>
          {summary && status === 'done' && !isExpanded ? (
            <p className="accreditation-wizard-step__summary">{summary}</p>
          ) : null}
        </div>
        {(status === 'active' || status === 'done') && (
          <span className="accreditation-wizard-step__chevron" aria-hidden="true">
            ▾
          </span>
        )}
      </button>
      {isExpanded && children ? (
        <div
          className="accreditation-wizard-step__body"
          id={`wizard-panel-${id}`}
          role="region"
          aria-labelledby={`wizard-trigger-${id}`}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}

const AccreditationWizardPage = () => {
  const { languageManifest } = useManagedLanguages();
  const [langKey, setLangKey] = useState('en');
  const [copy, setCopy] = useState(ACCREDITATION_WIZARD_EN);
  const [isTranslating, setIsTranslating] = useState(false);
  const [a11yScheme, setA11yScheme] = useState(getInitialA11yScheme);
  const [a11yPanelOpen, setA11yPanelOpen] = useState(false);

  const [started, setStarted] = useState(false);
  const [q1, setQ1] = useState(null);
  const [path1, setPath1] = useState({});
  const [path2, setPath2] = useState({});
  const [showPath2, setShowPath2] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [expandedStep, setExpandedStep] = useState('q1');

  useEffect(() => {
    document.documentElement.setAttribute('data-a11y-scheme', a11yScheme);
    try {
      const raw = localStorage.getItem(A11Y_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed.colorScheme = a11yScheme;
      localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      /* ignore */
    }
  }, [a11yScheme]);

  useEffect(() => {
    syncHoverZoomOthersToDom(getInitialHoverZoomOthers());
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (langKey === 'en') {
        setCopy(ACCREDITATION_WIZARD_EN);
        return;
      }
      setIsTranslating(true);
      try {
        const stored = await localizationService.getStored(langKey, 'accreditation_wizard_v1');
        if (stored && !cancelled) {
          setCopy({ ...ACCREDITATION_WIZARD_EN, ...stored });
        }
        const synced = await localizationService.sync(
          langKey,
          'accreditation_wizard_v1',
          ACCREDITATION_WIZARD_EN
        );
        if (!cancelled) setCopy({ ...ACCREDITATION_WIZARD_EN, ...synced });
      } catch (err) {
        console.error('[Wizard] localization', err);
        if (!cancelled) setCopy(ACCREDITATION_WIZARD_EN);
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [langKey]);

  useEffect(() => {
    if (q1 === 'c') {
      setOutcome('bespoke');
      setExpandedStep('outcome');
    }
  }, [q1]);

  useEffect(() => {
    if (q1 !== 'a') return;
    const result = scorePath1(path1);
    if (!result) return;
    if (result === 'path2') {
      setShowPath2(true);
      setOutcome(null);
      if (PATH1_KEYS.every((k) => path1[k])) {
        setExpandedStep('path2-q2a');
      }
    } else {
      setOutcome(result);
      setExpandedStep('outcome');
    }
  }, [path1, q1]);

  useEffect(() => {
    if (q1 === 'b') setShowPath2(true);
  }, [q1]);

  useEffect(() => {
    if (!showPath2 && q1 !== 'b') return;
    const result = scorePath2(path2);
    if (result) {
      setOutcome(result);
      setExpandedStep('outcome');
    }
  }, [path2, showPath2, q1]);

  const path1Questions = useMemo(
    () => [
      {
        id: 'path1-q1a',
        title: copy.path1_q1a_title,
        question: copy.path1_q1a_question,
        choices: [copy.path1_q1a_c1, copy.path1_q1a_c2, copy.path1_q1a_c3],
        key: 'q1a',
      },
      {
        id: 'path1-q1b',
        title: copy.path1_q1b_title,
        question: copy.path1_q1b_question,
        choices: [copy.path1_q1b_c1, copy.path1_q1b_c2, copy.path1_q1b_c3],
        key: 'q1b',
      },
      {
        id: 'path1-q1c',
        title: copy.path1_q1c_title,
        question: copy.path1_q1c_question,
        choices: [copy.path1_q1c_c1, copy.path1_q1c_c2, copy.path1_q1c_c3],
        key: 'q1c',
      },
    ],
    [copy]
  );

  const path2Questions = useMemo(
    () => [
      {
        id: 'path2-q2a',
        title: copy.path2_q2a_title,
        question: copy.path2_q2a_question,
        choices: [copy.path2_q2a_c1, copy.path2_q2a_c2, copy.path2_q2a_c3],
        key: 'q2a',
      },
      {
        id: 'path2-q2b',
        title: copy.path2_q2b_title,
        question: copy.path2_q2b_question,
        choices: [copy.path2_q2b_c1, copy.path2_q2b_c2, copy.path2_q2b_c3],
        key: 'q2b',
      },
      {
        id: 'path2-q2c',
        title: copy.path2_q2c_title,
        question: copy.path2_q2c_question,
        choices: [copy.path2_q2c_c1, copy.path2_q2c_c2, copy.path2_q2c_c3],
        key: 'q2c',
      },
    ],
    [copy]
  );

  const getStepStatus = (stepId, answered) => {
    if (outcome && stepId !== 'outcome') return 'done';
    if (expandedStep === stepId) return 'active';
    if (answered) return 'done';
    return 'locked';
  };

  const handleQ1 = (key) => {
    setQ1(key);
    setPath1({});
    setPath2({});
    setShowPath2(false);
    setOutcome(null);
    if (key === 'a') {
      setExpandedStep('path1-q1a');
    } else if (key === 'b') {
      setExpandedStep('path2-q2a');
    }
  };

  const handlePath1 = (key, value) => {
    setPath1((prev) => {
      const next = { ...prev, [key]: value };
      const idx = PATH1_KEYS.indexOf(key);
      if (idx < PATH1_KEYS.length - 1 && !next[PATH1_KEYS[idx + 1]]) {
        setExpandedStep(`path1-${PATH1_KEYS[idx + 1]}`);
      }
      return next;
    });
  };

  const handlePath2 = (key, value) => {
    setPath2((prev) => {
      const next = { ...prev, [key]: value };
      const idx = PATH2_KEYS.indexOf(key);
      if (idx < PATH2_KEYS.length - 1 && !next[PATH2_KEYS[idx + 1]]) {
        setExpandedStep(`path2-${PATH2_KEYS[idx + 1]}`);
      }
      return next;
    });
  };

  const handleRestart = () => {
    setStarted(false);
    setQ1(null);
    setPath1({});
    setPath2({});
    setShowPath2(false);
    setOutcome(null);
    setExpandedStep('q1');
  };

  const registerLevel = outcome ? outcomeToRegisterLevel(outcome) : null;
  const registerHref = registerLevel ? `/register?level=${registerLevel}` : null;
  const contactHref = `mailto:${copy.contact_email}?subject=${encodeURIComponent('IFIP Bespoke Diagnostic & Audit Services')}`;

  const outcomeContent = {
    bespoke: {
      title: copy.outcome_bespoke_title,
      body: copy.outcome_bespoke_body,
      cta: copy.outcome_bespoke_cta,
      href: contactHref,
      external: false,
    },
    foundation: {
      title: copy.outcome_foundation_title,
      body: copy.outcome_foundation_body,
      cta: copy.outcome_foundation_cta,
      href: registerHref,
    },
    certified: {
      title: copy.outcome_certified_title,
      body: copy.outcome_certified_body,
      cta: copy.outcome_certified_cta,
      href: registerHref,
    },
    global: {
      title: copy.outcome_global_title,
      body: copy.outcome_global_body,
      note: copy.outcome_global_note,
      cta: copy.outcome_global_cta,
      href: registerHref,
    },
  }[outcome];

  const translatingLabel =
    getTranslatingIndicator(langKey) || enMaster.ui?.localizing_indicator || 'Translating…';

  return (
    <div className="accreditation-wizard-root registration-root" dir={langKey === 'ar' ? 'rtl' : 'ltr'}>
      <div className="accreditation-wizard-header-bar">
        <Link to="/register" className="accreditation-wizard-link-back">
          ← {copy.btn_back_register}
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isTranslating && (
            <span className="translating-dots" style={{ fontSize: '0.65rem', color: '#138a8a' }}>
              {translatingLabel}
            </span>
          )}
          <select
            className="ifip-splash-lang-select"
            value={langKey}
            onChange={(e) => setLangKey(e.target.value)}
            aria-label="Language"
            style={{
              background: 'transparent',
              color: 'var(--a11y-page-text)',
              border: '1px solid var(--reg-glass-border-subtle)',
              borderRadius: '22px',
              padding: '6px 12px',
              fontSize: '0.85rem',
            }}
          >
            {Object.entries(languageManifest || baseLanguageManifest)
              .sort((a, b) =>
                (a[1].nativeName || a[1].label || a[0]).localeCompare(b[1].nativeName || b[1].label || b[0])
              )
              .map(([key, lang]) => (
                <option key={key} value={key} style={{ color: '#000' }}>
                  {lang.nativeName || lang.label || key}
                </option>
              ))}
          </select>
          <AccessibilityControls
            panelOpen={a11yPanelOpen}
            setPanelOpen={setA11yPanelOpen}
            scheme={a11yScheme}
            setScheme={setA11yScheme}
            labels={{
              btn: enMaster.ui?.a11y_btn || 'Accessibility',
              panelTitle: enMaster.ui?.a11y_panel_title || 'Colour scheme',
            }}
            rootClassName="ifip-a11y-control-root ifip-a11y-control-root--header"
            panelId="a11y-panel-wizard"
          />
        </div>
      </div>

      <div className="accreditation-wizard-shell">
        <div className="accreditation-wizard-card">
          {!started ? (
            <div className="accreditation-wizard-intro">
              <h1>{copy.page_title}</h1>
              <p>{copy.page_intro}</p>
              <div className="accreditation-wizard-actions">
                <button
                  type="button"
                  className="accreditation-wizard-btn accreditation-wizard-btn--primary"
                  onClick={() => {
                    setStarted(true);
                    setExpandedStep('q1');
                  }}
                >
                  {copy.btn_start}
                </button>
              </div>
            </div>
          ) : outcome && outcomeContent ? (
            <div className="accreditation-wizard-outcome fade-in">
              <h2>{outcomeContent.title}</h2>
              <p>{outcomeContent.body}</p>
              {outcomeContent.note ? (
                <p className="accreditation-wizard-outcome__note">{outcomeContent.note}</p>
              ) : null}
              <div className="accreditation-wizard-actions">
                {outcomeContent.href ? (
                  registerLevel ? (
                    <Link
                      to={outcomeContent.href}
                      className="accreditation-wizard-btn accreditation-wizard-btn--primary"
                    >
                      {outcomeContent.cta}
                    </Link>
                  ) : (
                    <a
                      href={outcomeContent.href}
                      className="accreditation-wizard-btn accreditation-wizard-btn--primary"
                    >
                      {outcomeContent.cta}
                    </a>
                  )
                ) : null}
                <button
                  type="button"
                  className="accreditation-wizard-btn accreditation-wizard-btn--secondary"
                  onClick={handleRestart}
                >
                  {copy.btn_restart}
                </button>
              </div>
            </div>
          ) : (
            <div className="accreditation-wizard-steps">
              <WizardStep
                id="q1"
                stepTitle={copy.step_q1_title}
                question={copy.step_q1_question}
                summary={q1 ? copy.answered_summary : null}
                status={getStepStatus('q1', q1)}
                isExpanded={expandedStep === 'q1'}
                onToggle={setExpandedStep}
              >
                <ChoiceGroup
                  choicePrefix={copy.choice_prefix}
                  value={q1 === 'a' ? 1 : q1 === 'b' ? 2 : q1 === 'c' ? 3 : null}
                  onSelect={(n) => handleQ1(n === 1 ? 'a' : n === 2 ? 'b' : 'c')}
                  choices={[
                    `${copy.q1_a_label} — ${copy.q1_a_desc}`,
                    `${copy.q1_b_label} — ${copy.q1_b_desc}`,
                    `${copy.q1_c_label} — ${copy.q1_c_desc}`,
                  ]}
                />
              </WizardStep>

              {q1 === 'a' &&
                path1Questions.map((pq, index) => {
                  const prevKey = PATH1_KEYS[index - 1];
                  const unlocked = index === 0 || path1[prevKey];
                  if (!unlocked) return null;
                  return (
                    <React.Fragment key={pq.id}>
                      {index === 0 && (
                        <div className="accreditation-wizard-path-banner">
                          <strong>{copy.path1_title}</strong>
                          {copy.path1_intro}
                        </div>
                      )}
                      <WizardStep
                        id={pq.id}
                        stepTitle={pq.title}
                        question={pq.question}
                        summary={path1[pq.key] ? copy.answered_summary : null}
                        status={getStepStatus(pq.id, path1[pq.key])}
                        isExpanded={expandedStep === pq.id}
                        onToggle={setExpandedStep}
                      >
                        <ChoiceGroup
                          choicePrefix={copy.choice_prefix}
                          value={path1[pq.key] || null}
                          onSelect={(v) => handlePath1(pq.key, v)}
                          choices={pq.choices}
                        />
                      </WizardStep>
                    </React.Fragment>
                  );
                })}

              {(q1 === 'b' || showPath2) &&
                path2Questions.map((pq, index) => {
                  const prevKey = PATH2_KEYS[index - 1];
                  const unlocked =
                    q1 === 'b'
                      ? index === 0 || path2[prevKey]
                      : PATH1_KEYS.every((k) => path1[k]) && (index === 0 || path2[prevKey]);
                  if (!unlocked) return null;
                  return (
                    <React.Fragment key={pq.id}>
                      {index === 0 && (
                        <div className="accreditation-wizard-path-banner">
                          <strong>{copy.path2_title}</strong>
                          {copy.path2_intro}
                        </div>
                      )}
                      <WizardStep
                        id={pq.id}
                        stepTitle={pq.title}
                        question={pq.question}
                        summary={path2[pq.key] ? copy.answered_summary : null}
                        status={getStepStatus(pq.id, path2[pq.key])}
                        isExpanded={expandedStep === pq.id}
                        onToggle={setExpandedStep}
                      >
                        <ChoiceGroup
                          choicePrefix={copy.choice_prefix}
                          value={path2[pq.key] || null}
                          onSelect={(v) => handlePath2(pq.key, v)}
                          choices={pq.choices}
                        />
                      </WizardStep>
                    </React.Fragment>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccreditationWizardPage;
